import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDatesForKpiTrends } from "../../utils/DateUtility";
import { fetchKpiCountsApi, fetchKpiTrendChartDataApi } from "../../api/KpiReportingApi";
import { startOfYear } from "date-fns";

type KPITrendsType = {
    trendsLoading: boolean | null;
    trendsLoadingError: string;

    pQuarter: {
        JV: any,
        ASR: any
    };
    p2pQuarter: {
        JV: any,
        ASR: any
    };

    kpiTrendChartLoading: boolean | null;
    kpiTrendChartLoadingError: string;
    kpiTrendChartData: {
        SOLE: any | null,
        PCIT: any | null
        DSS: any | null
        LOPC: any | null
    };
}

const initialState: KPITrendsType = {
    trendsLoading: null,
    trendsLoadingError: '',

    pQuarter: {
        JV: {
            SOLE: 0,
            PCIT: 0,
            DSS: 0,
            LOPC: 0,
        },
        ASR: {
            SOLE: 0,
            PCIT: 0,
            DSS: 0,
            LOPC: 0,
        }
    },
    p2pQuarter: {
        JV: {
            SOLE: 0,
            PCIT: 0,
            DSS: 0,
            LOPC: 0,
        },
        ASR: {
            SOLE: 0,
            PCIT: 0,
            DSS: 0,
            LOPC: 0,
        }
    },

    kpiTrendChartLoading: null,
    kpiTrendChartLoadingError: '',
    kpiTrendChartData: {
        SOLE: null,
        PCIT: null,
        DSS: null,
        LOPC: null
    }
}


const kpiTrendsSlice = createSlice({
    name: 'kpi_trends',
    initialState: initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(getKpiTrends.pending, (state) => {
                state.trendsLoading = true;
            })
            .addCase(getKpiTrends.fulfilled, (state, action) => {
                state.trendsLoading = false;
                state.pQuarter = {
                    JV: { ...action.payload.pqdataJV },
                    ASR: { ...action.payload.pqdataASR }
                }
                state.p2pQuarter = {
                    JV: { ...action.payload.p2pqdataJV },
                    ASR: { ...action.payload.p2pqdataASR }
                }
            })
            .addCase(getKpiTrends.rejected, (state) => {
                state.trendsLoading = false;
                state.trendsLoadingError = "Unable to fetch kpi trends.";
            })

            // get kpi trends chart date
            .addCase(getKpiTrendsChartData.pending, (state) => {
                state.kpiTrendChartLoading = true;
            })
            .addCase(getKpiTrendsChartData.fulfilled, (state, action) => {
                state.kpiTrendChartLoading = false;
                state.kpiTrendChartData = {
                    ...state.kpiTrendChartData,
                    ...action.payload
                }
            })
            .addCase(getKpiTrendsChartData.rejected, (state) => {
                state.kpiTrendChartLoading = false;
                state.kpiTrendChartLoadingError = "Unable to fetch kpi trends chart data.";
            })
    },
})

export const getKpiTrends = createAsyncThunk(
    'kpi_trends/getPreviousQuarter',
    async () => {
        const dates = getDatesForKpiTrends()

        const pqdataJV = await fetchKpiCountsApi({ ...dates.pq, businessEntity: 'JV' })
        const pqdataASR = await fetchKpiCountsApi({ ...dates.pq, businessEntity: 'ASR' })
        const p2pqdataJV = await fetchKpiCountsApi({ ...dates.p2pq, businessEntity: 'JV' })
        const p2pqdataASR = await fetchKpiCountsApi({ ...dates.p2pq, businessEntity: 'ASR' })

        return {
            pqdataJV,
            pqdataASR,
            p2pqdataJV,
            p2pqdataASR
        };
    }
)

export const getKpiTrendsChartData = createAsyncThunk(
    'kpi_trends/getKpiTrendsChartData',
    async (kpi: string) => {
        const endDate = new Date();
        const startDate = startOfYear(endDate);
        const year = endDate.getFullYear();
        const data = await fetchKpiTrendChartDataApi({ kpi, startDate, endDate, year })
        return {
            [kpi]: data
        };
    }
)

export default kpiTrendsSlice.reducer;

export const selectIfKpiTrendsLoading = (state: any) => state.kpi_trends.trendsLoading;
export const selectIfKpiTrendsLoadingError = (state: any) => state.kpi_trends.trendsLoading;
export const selectPQKpiTrends = (state: any) => state.kpi_trends.pQuarter;
export const selectP2PQKpiTrends = (state: any) => state.kpi_trends.p2pQuarter;

export const selectIfKpiTrendsChartLoading = (state: any) => state.kpi_trends.kpiTrendChartLoading;
export const selectIfKpiTrendsChartLoadingError = (state: any) => state.kpi_trends.kpiTrendChartLoadingError;
export const selectKpiTrendsChartData = (kpiName: string) => (state: any) => state.kpi_trends.kpiTrendChartData[kpiName];